import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Modal, Btn, Field, inputCls } from "@/components/cc";
import {
  LayoutDashboard, FolderSearch, Library, ScrollText, PenSquare,
  CheckSquare, History, LogOut, Radar, KeyRound, Siren, Clapperboard, Quote, Mail, Send, Image, Flag, Gavel, Handshake, GitBranch, Users, TrendingUp, Trophy, Film, Landmark,
} from "lucide-react";

const NAV = [
  { to: "/command-center", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/command-center/ghana-intel", label: "Ghana Intelligence", icon: Flag },
  { to: "/command-center/claim-tracker", label: "Political Claim Tracker", icon: Gavel },
  { to: "/command-center/promise-tracker", label: "Promise Tracker", icon: Handshake },
  { to: "/command-center/timelines", label: "Timeline Builder", icon: GitBranch },
  { to: "/command-center/entities", label: "Entity Profiles", icon: Users },
  { to: "/command-center/business-intelligence", label: "Business Intelligence", icon: TrendingUp },
  { to: "/command-center/sports-intelligence", label: "Sports Intelligence", icon: Trophy },
  { to: "/command-center/live-data", label: "Sports Live Data", icon: Radar },
  { to: "/command-center/entertainment", label: "Entertainment Intelligence", icon: Film },
  { to: "/command-center/culture", label: "Culture & Heritage Intelligence", icon: Landmark },
  { to: "/command-center/projects", label: "Research Projects", icon: FolderSearch },
  { to: "/command-center/sources", label: "Source Library", icon: Library },
  { to: "/command-center/claims", label: "Claims & Evidence", icon: ScrollText },
  { to: "/command-center/studio", label: "Article Studio", icon: PenSquare },
  { to: "/command-center/editorial", label: "Editorial Review", icon: CheckSquare },
  { to: "/command-center/crime", label: "Global Crime Watch", icon: Siren },
  { to: "/command-center/scripts", label: "Video Scripts", icon: Clapperboard },
  { to: "/command-center/quotes", label: "Quote Cards", icon: Quote },
  { to: "/command-center/media", label: "Media Library", icon: Image },
  { to: "/command-center/subscribers", label: "Subscribers", icon: Mail },
  { to: "/command-center/email", label: "Email Delivery", icon: Send },
  { to: "/command-center/audit", label: "Audit Trail", icon: History },
];

export default function CommandLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [pwModal, setPwModal] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });

  const doLogout = async () => {
    await logout();
    nav("/command-center/login");
  };

  const changePw = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Password changed. Please sign in again.");
      setPwModal(false);
      await logout();
      nav("/command-center/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="dark grain min-h-screen bg-[#0A0A0A] text-zinc-100 font-ui flex">
      <aside className="w-[248px] shrink-0 border-r border-zinc-800/80 bg-[#0C0C0C] flex flex-col fixed h-screen z-10">
        <div className="px-5 py-5 border-b border-zinc-800/80">
          <div className="flex items-center gap-2">
            <Radar className="h-5 w-5 text-emerald-400" />
            <div>
              <div className="font-tactical font-black text-sm leading-none tracking-tight">BE-APUO</div>
              <div className="label-caps text-zinc-600 text-[0.6rem]">Command Center</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`sidebar-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
                  isActive
                    ? "bg-zinc-800/60 text-white border-l-2 border-emerald-400"
                    : "text-zinc-400 hover:text-white hover:bg-zinc-900"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-zinc-800/80">
          <div className="px-2 py-2">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-[0.7rem] text-emerald-400 font-mono-data uppercase">{user?.roles?.join(", ")}</div>
          </div>
          <button
            data-testid="change-password-btn"
            onClick={() => setPwModal(true)}
            className="mt-1 w-full flex items-center gap-2 px-3 py-2 rounded-sm text-sm text-zinc-400 hover:text-white hover:bg-zinc-900 transition-colors"
          >
            <KeyRound className="h-4 w-4" /> Change password
          </button>
          <button
            data-testid="logout-btn"
            onClick={doLogout}
            className="mt-1 w-full flex items-center gap-2 px-3 py-2 rounded-sm text-sm text-zinc-400 hover:text-red-400 hover:bg-zinc-900 transition-colors"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 ml-[248px] relative z-[2]">
        <Outlet />
      </main>

      <Modal open={pwModal} onClose={() => setPwModal(false)} title="Change Password">
        <form onSubmit={changePw} className="space-y-4">
          <Field label="Current password">
            <input data-testid="current-password-input" type="password" required className={inputCls}
              value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} />
          </Field>
          <Field label="New password (min 12 chars)">
            <input data-testid="new-password-input" type="password" required minLength={12} className={inputCls}
              value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} />
          </Field>
          <p className="text-xs text-zinc-500">Changing your password revokes all active sessions.</p>
          <Btn data-testid="change-password-submit" type="submit" className="w-full justify-center">Update Password</Btn>
        </form>
      </Modal>
    </div>
  );
}
